import React, { useEffect } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import { hot } from "react-hot-loader/root";
import { AbacProvider } from "react-abac";
import styled, { ThemeProvider } from "styled-components";
import "react-tooltip/dist/react-tooltip.css";
import { Transition, TransitionGroup } from "react-transition-group";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RoutesEnum, StatusStateEnum } from "~/types/enums";
import Layout from "~/components/Layout";
import Auth from "~/views/public/Auth";
import rules from "~/lib/rules";
import authActionCreators from "~/store/auth/actions";
import authSelectors from "~/store/auth/selectors";
import NotFound from "~/views/private/NotFound";
import Profile from "~/views/private/Profile";
import SidebarProvider from "~/components/SidebarProvider";
import Members from "~/views/private/Members";
import Member from "~/views/private/Member";
import { enterView, exitView } from "~/lib/animations";
import Spinner from "~/components-shared/Spinner";
import { Tooltip } from "react-tooltip";
import moment from "moment";
import ErrorView from "~/views/private/ErrorView";
import initWebsocket from "~/lib/websockets";
import AppStatusModal from "~/components/AppStatusModal";
import Toasts from "~/components/Toasts";
import GlobalStyle from "~/components/GlobalStyle";
import useUserTheme from "~/hooks/theme";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import qs from "qs";
import Integrations from "~/views/private/Integrations";
import Overview from "~/views/private/Overview";
import KnowledgeGraph from "~/views/private/KnowledgeGraph";
const Wrapper = styled.div`
  height: 100%;
`;
const Center = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
`;
const StyledToasts = styled(Toasts)`
  position: fixed;
  z-index: 1000;
  bottom: 20px;
  left: 20px;
`;
moment.locale("en", {
  week: {
    dow: 1
  },
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "seconds",
    ss: "%s seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years"
  }
});
const App = () => {
  const user = useSelector(authSelectors.getUser);
  const sessionStatus = useSelector(authSelectors.getSessionStatus);
  const integrationStatus = useSelector(integrationSelectors.getStatus);
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const userTheme = useUserTheme(user?.theme);
  const {
    authToken
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });
  useEffect(() => {
    if (sessionStatus === StatusStateEnum.IDLE) {
      if (authToken) {
        dispatch(authActionCreators.updateTokenStarted(authToken));
      }
      dispatch(authActionCreators.continueSessionStarted());
    }
  }, [dispatch, sessionStatus, authToken]);
  useEffect(() => {
    if (user) {
      initWebsocket(store);
    }
  }, [store, user]);
  useEffect(() => {
    if (user && integrationStatus === StatusStateEnum.IDLE) {
      dispatch(integrationsActionCreators.fetchStarted());
      dispatch(integrationsActionCreators.fetchMemberStarted());
    }
  }, [integrationStatus, user]);
  if (sessionStatus === StatusStateEnum.LOADING || sessionStatus === StatusStateEnum.IDLE) {
    return /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(ThemeProvider, {
      theme: userTheme
    }, /*#__PURE__*/React.createElement(Spinner, null)));
  }
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: userTheme
  }, /*#__PURE__*/React.createElement(GlobalStyle, null), /*#__PURE__*/React.createElement(ErrorView, null, user ? /*#__PURE__*/React.createElement(AbacProvider, {
    user: user,
    roles: [user.role.id],
    rules: rules,
    permissions: user.authorities
  }, /*#__PURE__*/React.createElement(SidebarProvider, null, /*#__PURE__*/React.createElement(Layout, null, /*#__PURE__*/React.createElement(Switch, {
    location: location
  }, /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.PROFILE,
    component: Profile
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.OVERVIEW,
    component: Overview
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.KNOWLEDGE_GRAPH,
    component: KnowledgeGraph
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.INTEGRATIONS,
    component: Integrations
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBERS,
    component: Members
  }), /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.MEMBER,
    component: Member
  }), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    component: NotFound
  })))), /*#__PURE__*/React.createElement(AppStatusModal, null)) : /*#__PURE__*/React.createElement(TransitionGroup, {
    component: null
  }, /*#__PURE__*/React.createElement(Transition, {
    key: location.key,
    onEnter: (node, appears) => enterView(node, appears),
    onExit: node => exitView(node),
    timeout: {
      enter: 750,
      exit: 400
    },
    appear: true
  }, /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    exact: true,
    path: RoutesEnum.LOG_IN,
    component: Auth
  }), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    component: Auth
  })))), /*#__PURE__*/React.createElement(Tooltip, {
    id: "general-tooltip",
    place: "bottom",
    delayShow: 500
  }), /*#__PURE__*/React.createElement(StyledToasts, null))));
};
export default App;